import { createSlice } from "@reduxjs/toolkit";

import { DEFAULT_QUIZ } from "../../constants";
import { dispatch, store } from "../index";
import { emit } from "../../utils/socket";

import { preloadQuestions } from "./question";

import { checkAnswer } from "@lrg/lln-quiz-common/lib/resultCalculator";

import { saveLearnerForm, submitLearnerAnswer, completeLater, getLearner } from "../../utils/api/learner";

const initialState = {
	details: null,
	showModuleBreak: false,
	modules: {
		previous: "",
		new: "",
	},
    showResumePage: false
};

const slice = createSlice({
	name: "learner",
	initialState,
	reducers: {
		setRestoredLearner(state, action) {
			if (action.payload?.learner) {
				state.details = { ...action.payload.learner };
			}
		},
		updateLearner(state, action) {
			state.details = { ...state.details, ...action.payload };
		},
		updateAnswer(state, action) {
			state.details = {
				...state.details,
				answers: [...action.payload.answer],
			};
			state.details.state.currentQuestion =
				action.payload.nextQuestionRequest;
		},
		setComplete(state) {
			state.details = { ...state.details, complete: true };
		},
		setModuleBreak(state, action) {
			state.showModuleBreak = action.payload.show || false;
			state.modules = action.payload.modules || {
				previous: "",
				new: "",
			};
		},
		setResumePage(state, action) {
			state.showResumePage = action.payload.show || false;
		},
	},
});

export default slice.reducer;

export function restoreLearner(invite = null) {
	return async () => {
		try {
			// register quiz session
            await emit("learner:invite", { invite });
            const data = await getLearner(invite);

			// set preload questions
			await dispatch(preloadQuestions());

			// get next question if quiz started
			if ((data?.learner?.answers || []).length > 0) {
				let formatAnswers = [];
				(data?.learner?.answers || []).forEach((a) => {
					formatAnswers.push({
						questionId: a.questionId,
						answer: { ...a },
					});
				});

                if (["acsf3", "acsf4", "vfh"].indexOf(data?.learner?.testLevel) !== -1) {
                    dispatch(slice.actions.setResumePage({show: true}));
				}
				// await dispatch(getNextQuestion(formatAnswers));
				// console.log('restore learner', data);
			}

			dispatch(slice.actions.setRestoredLearner(data));
			return data;
		} catch (err) {
			console.log(err);
		}
	};
}

/**
 * submit learner form
 * updates the learner details
 */
export function submitLearner(params, customForm, quizId = DEFAULT_QUIZ) {
	return async () => {
		try {
			const learnerInfo = store.getState().learner?.details || null;
			let custom = [];
			if (Object.keys(customForm).length > 0) {
				custom = Object.entries(customForm).map((form) => {
					let v = {};
					v[form[0]] = form[1];
					return v;
				});
			}

			let testLevel =
				["acsf3", "acsf3shortcourse", "acsf4", "vfh"].indexOf(quizId) !== -1
					? quizId
					: DEFAULT_QUIZ;
			if (learnerInfo?.testLevel) {
				testLevel = learnerInfo?.testLevel;
			}

			const apiParams = {
				...params,
				quizId: testLevel || DEFAULT_QUIZ,
				custom,
				system: "acsf3shortcourse-quiz",
			};

			if (learnerInfo) {
				apiParams.invite = learnerInfo.inviteCode;
			}

			const submitAPI = await saveLearnerForm(apiParams);
			const data = submitAPI?.data?.data || null;

			await dispatch(preloadQuestions());
			dispatch(slice.actions.updateLearner(data || null));
			return data?.inviteCode || null;
		} catch (err) {
			console.log(err);
		}
	};
}

/**
 * submit answer
 */
export function submitAnswer(question, answer, startTime, invite = null) {
	return async () => {
		try {
			// const question = store.getState().question;
			const learner = store.getState().learner;

			const correct =
				checkAnswer(question?.questionData.answer, answer) || false;

			let submittedAnswers = learner?.details?.answers || [];
			let formatSubmittedAnswers = [];
			submittedAnswers.forEach((ans) => {
				formatSubmittedAnswers.push({
					questionId: ans.questionId,
					answer: ans,
				});
			});

			const formatAnswer = {
				answer,
				correct,
				questionId: question?.questionData?.id,
				startTime,
				submitTime: Date.now(),
				version: question?.questionData?.version || null,
			};

			const submittedAnswer = await submitLearnerAnswer({
				answer: formatAnswer,
				system: "acsf3shortcourse-quiz",
				invite,
			});

			const data = submittedAnswer?.data?.data || null;
			const nextQuestionRequest = data?.nextQuestion || "false";

			if (
				nextQuestionRequest !== "false" &&
				["acsf3", "acsf4"].indexOf(learner?.details?.testLevel) !== -1
			) {
				const newModule = nextQuestionRequest.split("-")[0];
				const currentModule =
					learner?.details?.state?.currentQuestion.split("-")[0];
				if (newModule !== currentModule) {
					dispatch(
						slice.actions.setModuleBreak({
							show: true,
							modules: {
								previous: currentModule,
								new: newModule,
							},
						})
					);
				}
			}
			// UPDATE ANSWER
			let answersList = learner?.details?.answers || [];
			answersList = [...answersList, { ...formatAnswer }];
			dispatch(
				slice.actions.updateAnswer({
					answer: answersList,
					nextQuestionRequest,
				})
			);
			if (nextQuestionRequest === "false") {
				dispatch(slice.actions.setComplete());
			}
			return nextQuestionRequest;
		} catch (err) {
			console.log(err);
		}
	};
}

export function toggleModuleBreak(show = false) {
	return async () => {
		dispatch(slice.actions.setModuleBreak({ show }));
	};
}

export function confirmCompleteLater (invite = null) {
    return async () => {
        return await completeLater({
            invite
        });
    }
}

export function toggleResumePage(show = false) {
	return async () => {
		dispatch(slice.actions.setResumePage({ show }));
	};
}
